import { CarrierType, RailcarTrackPositionResponseDto, RestowDto } from '@planning/app/api'
import { RailcarTrackPositionItemStore } from '@planning/rt-stores/railTrack/RailcarTrackPositionItemStore'
import { RailTrackItemStore } from '@planning/rt-stores/railTrack/RailTrackItemStore'
import { generalCargoOrderUploadParsingService, orderListParsingService } from '@planning/services'
import { CarrierVisitDirection } from '@tom-ui/integration'
import { action, makeObservable, observable } from 'mobx'
import {
  IGeneralCargoUploadOrderDto,
  IOrderDto,
  IOrderList,
  OrderListUploadViewStoreV2,
} from './OrderListUploadViewStoreV2'

export type VesselVisitBaplieParseResult = {
  vesselName: string | undefined
  imoNumber: string | undefined
  callSign: string | undefined
}

export type BaplieParseResult = {
  vesselVisit: VesselVisitBaplieParseResult | undefined
  orders: IOrderDto[]
  errors: string[]
}

// [JH] TODO: Test store
export class OrderListUploadParsingStore {
  unloCodes: string[] = []

  file?: File
  uploadFailedReason?: string
  uploadWarningMessages: string[] = []

  parseBaplie?: (file: File) => Promise<BaplieParseResult>
  isBaplieParserReplacementFFEnabled = false

  constructor(
    private parentStore: OrderListUploadViewStoreV2,
    private railTrackStore: RailTrackItemStore,
    private railcarTrackPositionItemStore: RailcarTrackPositionItemStore,
  ) {
    makeObservable(this, {
      unloCodes: observable,
      file: observable,
      uploadFailedReason: observable,
      uploadWarningMessages: observable,

      setUNLOCodes: action,
      setFile: action,
      setUploadFailedReason: action,
      setUploadWarningMessage: action,
    })
  }

  setUNLOCodes = (unloCodes: string[]) => {
    this.unloCodes = unloCodes
  }

  setFile = (file?: File) => {
    this.file = file
  }

  setUploadFailedReason = (reason?: string) => {
    this.uploadFailedReason = reason
  }

  // [JH] TODO: test
  parseOrderList = async (isRestow?: boolean) => {
    const { selectedVisitId: visitId, direction, selectedCarrierType: visitType } = this.parentStore

    if (!visitId || !direction || !this.file) return

    const { orderList, restowList, generalCargoOrders } = await this.tryParseFile(
      visitId,
      visitType,
      direction,
      this.file,
      this.unloCodes,
      isRestow,
    )

    if (isRestow) {
      this.parentStore.dataStore.setRestowUnits(restowList ?? [])
    } else if (this.parentStore.isGeneralCargoVisit) {
      this.parentStore.setGeneralCargoUploadOrders(generalCargoOrders ?? [])
    } else {
      this.parentStore.dataStore.setOrdersFromUpdate(orderList?.orders ?? [])
      this.parentStore.setGeneralCargoUploadOrders([])
      this.parentStore.dataStore.setRemainOnBoard(orderList?.remainOnboard ?? [])
      this.parentStore.dataStore.setNonNumeric(orderList?.nonNumeric ?? [])
      this.parentStore.dataStore.setVesselVisit(orderList?.vesselVisit)
    }
  }

  private tryParseFile = async (
    visitId: number,
    visitType: CarrierType,
    direction: CarrierVisitDirection,
    file: File,
    unloCodes: string[],
    isRestow?: boolean,
  ): Promise<{
    orderList: IOrderList | undefined
    restowList: RestowDto[] | undefined
    generalCargoOrders: IGeneralCargoUploadOrderDto[] | undefined
  }> => {
    const relatedRailcars: RailcarTrackPositionResponseDto[] | undefined =
      visitType === CarrierType.Train
        ? this.railcarTrackPositionItemStore.getRailcarsTrackPositionByRailVisitId(
            visitId,
            direction,
          )
        : undefined

    if (file.name.toLocaleLowerCase().endsWith('.csv')) {
      if (isRestow) {
        const restowList = await orderListParsingService.parseRestowCsv(file)

        return { orderList: undefined, restowList, generalCargoOrders: undefined }
      } else {
        const { orderList, generalCargoOrders } = await this.tryParseOrderCsv(
          visitId,
          visitType,
          direction,
          file,
          relatedRailcars,
        )

        return { orderList, restowList: undefined, generalCargoOrders }
      }
    } else {
      const orderList = await this.tryParseBaplie(visitId, direction, file, unloCodes)
      return { orderList, restowList: undefined, generalCargoOrders: undefined }
    }
  }

  private tryParseOrderCsv = async (
    visitId: number,
    visitType: CarrierType,
    direction: CarrierVisitDirection,
    file: File,
    relatedRailcars: RailcarTrackPositionResponseDto[] | undefined,
  ) => {
    if (this.parentStore.isGeneralCargoVisit) {
      const generalCargoOrders = await generalCargoOrderUploadParsingService.parseCsv(
        file,
        visitType,
        this.railTrackStore.activeRailTrackNameMap,
        relatedRailcars,
      )

      this.setUploadWarningMessage([])

      return { generalCargoOrders }
    }

    const { orderList, warningMessages } = await orderListParsingService.parseCsv(
      visitId,
      direction,
      file,
      visitType,
      this.railTrackStore.activeRailTrackNameMap,
      this.parentStore.railVisit?.assignedRailTrackIds,
      relatedRailcars,
    )

    this.setUploadWarningMessage(warningMessages)

    return { orderList }
  }

  private tryParseBaplie = async (
    visitId: number,
    direction: CarrierVisitDirection,
    file: File,
    unloCodes: string[],
  ) => {
    if (!unloCodes.length) return

    const { mappedList, errors } = await this.parseBaplieAndMapToOrderList(
      visitId,
      direction,
      unloCodes,
      file,
    )

    if (errors.length) {
      this.setUploadFailedReason(errors[0])
      return
    }

    return mappedList
  }

  private findRelevantOrders = (
    orderDtos: IOrderDto[],
    direction: CarrierVisitDirection,
    unloCodes: string[],
  ) => {
    const relevantOrders = orderDtos.filter(o => {
      if (direction === CarrierVisitDirection.Inbound)
        return o.portOfDischarge && unloCodes.includes(o.portOfDischarge)

      return o.portOfLoading && unloCodes.includes(o.portOfLoading)
    })

    const remainOnBoards: IOrderDto[] = orderDtos.filter(
      o =>
        (!o.portOfDischarge || !unloCodes.includes(o.portOfDischarge)) &&
        (!o.portOfLoading || !unloCodes.includes(o.portOfLoading)),
    )

    return { relevantOrders, remainOnBoards }
  }

  reset = () => {
    this.setUNLOCodes([])
    this.setFile()
    this.setUploadFailedReason()
    this.setUploadWarningMessage()
  }

  setUploadWarningMessage = (messages?: string[]) => {
    this.uploadWarningMessages = messages ?? []
  }

  private async parseBaplieAndMapToOrderList(
    visitId: number,
    direction: CarrierVisitDirection,
    unloCodes: string[],
    file: File,
  ) {
    const mappedList: IOrderList = {
      carrierVisitId: visitId,
      direction: direction,
      orders: [],
      remainOnboard: [],
      nonNumeric: [],
    }

    if (!this.parseBaplie) {
      // Should not happen
      return {
        mappedList,
        errors: ['Parsing function not initialized - cannot parse BAPLIE file'],
      }
    }

    const { orders, vesselVisit, errors } = await this.parseBaplie(file)

    this.populateOrderList(orders, direction, unloCodes, mappedList, vesselVisit)

    return { mappedList, errors }
  }

  populateOrderList(
    orders: IOrderDto[],
    direction: CarrierVisitDirection,
    unloCodes: string[],
    mappedList: IOrderList,
    vesselVisit?: VesselVisitBaplieParseResult,
  ) {
    if (orders?.length) {
      const { relevantOrders, remainOnBoards } = this.findRelevantOrders(
        orders,
        direction,
        unloCodes,
      )

      mappedList.orders = relevantOrders.filter(o => o.containerNumber.trim() !== '')
      mappedList.nonNumeric = relevantOrders.filter(o => o.containerNumber.trim() === '')
      mappedList.remainOnboard = remainOnBoards
      mappedList.vesselVisit = vesselVisit
    }
  }
}
